import { useState } from "react";
import { Share2, MessageCircle, Send, Mail, MessageSquare, Copy, Download, FileText, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePreparedFile, downloadFile, fetchShareLink, isOfficeFile, FILE_SHARE_UNSUPPORTED, OFFICE_SHARE_BLOCKED } from "@/lib/share";

const SENSITIVE = ["Documentos", "Senhas", "Saúde", "Financeiro", "Dinheiro"];

export function ShareMemory({ memory, isPremium }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("text");
  const [confirming, setConfirming] = useState(false);
  const [unsupported, setUnsupported] = useState(null);
  const images = (memory.attachments || []).filter((a) => a.category === "image" || a.content_type?.startsWith("image/"));
  const docs = (memory.attachments || []).filter((a) => !(a.category === "image" || a.content_type?.startsWith("image/")));
  const sensitive = SENSITIVE.includes(memory.category) || (memory.attachments || []).length > 0;
  const body = memory.original_text || memory.summary || "";
  const text = mode === "text" ? body : `${memory.title}\n\n${body}`;
  const withFile = mode === "image" || mode === "file";
  const att = mode === "image" ? images[0] : mode === "file" ? docs[0] : null;
  const prepared = usePreparedFile(att, open && withFile);
  const busy = withFile && prepared.loading;

  const reset = (v) => { setOpen(v); if (!v) { setConfirming(false); setUnsupported(null); } };

  const shareFile = async () => {
    const { file, shareable, error } = prepared;
    if (error || !file) return toast.error(error || "Arquivo ainda não está pronto.");
    if (!shareable) { setUnsupported(file); return; }
    try {
      await navigator.share({ files: [file], title: memory.title, text: mode === "image" ? text : memory.title });
      reset(false);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(e?.name === "NotAllowedError" ? "O navegador bloqueou o compartilhamento. Toque em Compartilhar novamente." : "Não foi possível compartilhar o arquivo.");
    }
  };
  const shareLink = async () => {
    try {
      const url = await fetchShareLink(att);
      const msg = `${memory.title} — ${att.filename}\n${url}\n(link válido por 24 horas)`;
      if (navigator.share) { await navigator.share({ title: att.filename, text: msg }); reset(false); return; }
      await navigator.clipboard?.writeText(msg);
      toast.success("Link copiado. Cole no app desejado.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Não foi possível gerar o link."); }
  };

  const shareText = async () => {
    const payload = mode === "title" ? { title: memory.title, text } : { text };
    try {
      if (navigator.share) { await navigator.share(payload); reset(false); return; }
      await navigator.clipboard?.writeText(text);
      toast.success("Texto copiado. Escolha um app abaixo.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Não foi possível compartilhar."); }
  };

  const doShare = () => { setConfirming(false); return withFile ? shareFile() : shareText(); };
  const start = () => (sensitive ? setConfirming(true) : doShare());

  const enc = encodeURIComponent(text);
  const links = [
    ["WhatsApp", MessageCircle, `https://wa.me/?text=${enc}`, "share-whatsapp"],
    ["Telegram", Send, `https://t.me/share/url?url=%20&text=${enc}`, "share-telegram"],
    ["E-mail", Mail, `mailto:?subject=${encodeURIComponent(memory.title)}&body=${enc}`, "share-email"],
    ["SMS", MessageSquare, `sms:?body=${enc}`, "share-sms"],
  ];
  const opt = (id, label, detail, disabled) => (
    <label key={id} className={`flex min-w-0 items-center gap-3 rounded-2xl border px-3 py-2.5 text-sm ${disabled ? "opacity-40" : "cursor-pointer"}`} style={{ borderColor: mode === id ? "#0A6373" : "var(--ga-border)" }}>
      <input type="radio" name="share-mode" disabled={disabled} checked={mode === id} onChange={() => { setMode(id); setUnsupported(null); }} data-testid={`share-mode-${id}`} className="shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block">{label}</span>
        {detail && <span className="ga-muted block truncate text-xs" title={detail} data-testid={`share-mode-${id}-detail`}>{detail}</span>}
      </span>
    </label>
  );
  const linkCls = "ga-surface flex min-w-0 flex-col items-center gap-1 rounded-2xl px-1 py-2 text-[11px]";

  return (
    <>
      <button onClick={() => setOpen(true)} className="ga-surface flex h-10 w-10 items-center justify-center rounded-full" aria-label="Compartilhar" data-testid="memory-share-button"><Share2 size={20} /></button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm overflow-x-hidden rounded-3xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]" data-testid="share-dialog">
          <DialogTitle>Compartilhar</DialogTitle>
          <DialogDescription className="break-words text-sm">Escolha o que enviar. Nada é compartilhado sem sua confirmação e nenhum dado da sua conta vai junto.</DialogDescription>
          <div className="min-w-0 space-y-2">
            {opt("text", "Somente o texto")}
            {opt("title", "Título + texto")}
            {opt("image", "Texto + imagem", images[0]?.filename, !images.length)}
            {opt("file", "Arquivo (anexo real)", docs[0]?.filename, !docs.length || !isPremium)}
          </div>
          {withFile && !unsupported && (
            <p className="ga-muted flex items-start gap-1.5 break-words text-xs" data-testid="share-file-hint"><FileText size={14} className="mt-0.5 shrink-0" /> O arquivo será anexado no app que você escolher (WhatsApp, Telegram, Gmail, Drive…).</p>
          )}
          {unsupported ? (
            <div className="min-w-0 rounded-2xl p-3 text-sm" style={{ background: "#FFF4E5", color: "#7A4A00" }} data-testid="share-file-unsupported">
              <p className="break-words">{isOfficeFile(att?.filename) ? OFFICE_SHARE_BLOCKED : FILE_SHARE_UNSUPPORTED}</p>
              <button onClick={() => downloadFile(unsupported, att?.filename)} className="ga-btn-primary mt-3 w-full" data-testid="share-file-download"><Download size={18} /> Baixar arquivo</button>
              <button onClick={shareLink} className="ga-btn-ghost mt-2 w-full" data-testid="share-file-link"><Link2 size={18} /> Enviar link para baixar (24 h)</button>
            </div>
          ) : !confirming ? (
            <button onClick={start} disabled={busy} className="ga-btn-primary w-full" data-testid="share-confirm-button">{busy ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />} {busy ? "Preparando arquivo..." : withFile ? "Compartilhar arquivo" : "Compartilhar"}</button>
          ) : (
            <div className="min-w-0 rounded-2xl p-3 text-sm" style={{ background: "#FFF4E5", color: "#7A4A00" }} data-testid="share-sensitive-confirm">
              <p className="break-words">Este conteúdo pode ser sensível ({memory.category}{withFile ? ", com anexo" : ""}). Confirma o compartilhamento?</p>
              <div className="mt-2 flex gap-2"><button onClick={() => setConfirming(false)} className="ga-btn-ghost flex-1 py-2" data-testid="share-sensitive-cancel">Cancelar</button><button onClick={doShare} className="ga-btn-primary flex-1 py-2" data-testid="share-sensitive-ok">Sim, compartilhar</button></div>
            </div>
          )}
          <div className="grid grid-cols-4 gap-2 pt-1" data-testid="share-fallback-links">
            {links.map(([label, Icon, href, tid]) => withFile
              ? <button key={tid} type="button" onClick={start} disabled={busy} className={linkCls} data-testid={tid}><Icon size={16} /> <span className="truncate">{label}</span></button>
              : <a key={tid} href={href} target="_blank" rel="noreferrer" className={linkCls} data-testid={tid}><Icon size={16} /> <span className="truncate">{label}</span></a>)}
          </div>
          {!withFile && <button onClick={async () => { await navigator.clipboard?.writeText(text); toast.success("Copiado."); }} className="ga-muted flex w-full items-center justify-center gap-1 text-xs" data-testid="share-copy"><Copy size={12} /> Copiar texto</button>}
        </DialogContent>
      </Dialog>
    </>
  );
}
